/**
 * @param {IKEASlider} slider 
 * @param {HTMLElement} element 
 */
function mouseControl(slider, element){
   
   let down = false;
   let dragged = false;
   let startX = 0;
   let velocity = 0;

   let reset = ()=>{
      let child = slider.getSlideFromOffset(slider.offsetX + velocity * 10);
      slider.setTargetX(child.offsetLeft);
      slider.clampTargetX();
   };

   element.addEventListener("mousedown", ev => {
      down = true;
      dragged = false;
      startX = ev.pageX;
      ev.preventDefault();
   });
   element.addEventListener("mouseup", ev => {
      down = false;
      reset();
   });
   element.addEventListener("mouseleave", ev => {
      down = false;
      reset();
   });
   element.addEventListener("click", ev => {
      if(dragged){
         ev.preventDefault();
      }
   });
   element.addEventListener("mousemove", ev => {
      if(!down) return;

      let delta = startX - ev.pageX;
      startX = ev.pageX;

      velocity = delta;

      if(delta !== 0){
         dragged = true;
      }
      
      slider.moveX(delta);
   });
}

/**
 * @param {IKEASlider} slider 
 * @param {HTMLElement} element 
 */
function touchControl(slider, element){
   
   let down = false;
   let dragged = false;

   let touchIndex = 0;
   
   let startX = 0;
   let velocity = 0;
   let distance = 0;
   let eatDistance = 0;

   let reset = ()=>{
      let child = slider.getSlideFromOffset(slider.offsetX + velocity * 10);
      slider.setTargetX(child.offsetLeft);
      slider.clampTargetX();
   };

   element.addEventListener("touchstart", ev => {
      
      if(down) return;
      
      if(ev.changedTouches.length == 0) return;
      
      let touch = ev.changedTouches.item(0);
      
      touchIndex = touch.identifier;
      
      down = true;
      dragged = false;
      distance = 0;
      eatDistance = 0;
      startX = touch.pageX;
   });
   element.addEventListener("touchend", ev => {
      for(let i = 0; i < ev.changedTouches.length; i++){
         let touch = ev.changedTouches.item(i);

         if(touch.identifier === touchIndex){
            down = false;
            reset();

            return;
         }
      }
   });
   element.addEventListener("touchmove", ev => {
      if(!down) return;

      for(let i = 0; i < ev.changedTouches.length; i++){
         let touch = ev.changedTouches.item(i);

         if(touch.identifier !== touchIndex){
            continue;
         }
         
         let delta = startX - touch.pageX;
         startX = touch.pageX;

         velocity = delta;

         distance += Math.abs(delta);
         eatDistance += delta;
         
         if(distance >= 40 && dragged == false){
            dragged = true;
            slider.moveX(eatDistance);
         }
         
         if(dragged){
            slider.moveX(delta); 
            ev.preventDefault();  
         }
      }
   });
   
   element.addEventListener("click", ev => {
      if(dragged){
         ev.preventDefault();
      }
   });
}

/**
 * 
 * @param {IKEASlider} slider 
 * @param {HTMLElement} element 
 */
function resizeControl(slider, element){
   window.addEventListener("resize", _=>slider.snap());
}

/**
 * It is claled IKEA slider because it is do it yourself
 */
export default class IKEASlider{

   /**
    * @param {HTMLElement} element 
    */
   constructor(element){
      this.container = element;

      this.offsetX = 0;
      this.targetX = 0;

      this.animationFrame = -1;

      touchControl(this, this.container);
      mouseControl(this, this.container);
      resizeControl(this, this.container);
   }

   animate(){
      let distance = this.offsetX - this.targetX;

      this.offsetX -= distance * 0.1;

      this.apply();

      if(Math.abs(distance) < 0.5){
         this.offsetX = this.targetX;
         this.apply(); // Apply it twice (because the offset changed)
      }else{
         // Request the next frame
         this._requestAnimationFrame();
      }
   }

   /** @param {number} delta */
   moveX(delta){
      if(this.offsetX < this.scrollPixelStart) delta /= 2;
      if(this.offsetX > this.scrollPixelEnd) delta /= 2;

      this.offsetX += delta;
      this.targetX = this.offsetX;

      this.animate();
   }

   /** @param {number} value */
   setOffsetX(value){
      this.offsetX = value;
      this.targetX = value;
      this.animate();
   }

   /** 
    * @param {number} value
    */
   setTargetX(value){
      this.targetX = value;

      this._requestAnimationFrame();
   }

   clampTargetX(){
      if(this.targetX > this.scrollPixelEnd) this.targetX = this.scrollPixelEnd;
      if(this.targetX < this.scrollPixelStart) this.targetX = this.scrollPixelStart;
   }

   apply(){
      let transform = "translateX(" + Math.round(-this.offsetX) + "px)";

      for(let i = 0; i < this.container.children.length; i++){
         let child = this.container.children[i];

         child.style.transform = transform;
      }
   }

   next(){
      this.selectSlide(this.selectedIndex + 1);
   }

   previous(){
      this.selectSlide(this.selectedIndex - 1);
   }

   snap(){
      this.selectSlide(this.getSlideIndexFromOffset(this.offsetX));
   }

   /** @param {number} index */
   selectSlide(index){
      if(index < 0) index = 0;
      if(index >= this.slideCount - 1) index = this.slideCount - 1;

      this.setTargetX(this.container.children[index].offsetLeft);
      this.clampTargetX();
   }

   /** @returns {number} */
   get slideCount(){
      return this.container.children.length;
   }
   /** @returns {number} */
   get selectedIndex(){
      return this.getSlideIndexFromOffset(this.targetX);
   }
   /** @returns {HTMLElement} */
   get selectedSlide(){
      return this.getSlideFromOffset(this.targetX);
   }
   /** @returns {number} */
   get scrollPixelStart(){
      return 0;
   }
   /** @returns {number} */
   get scrollPixelEnd(){
      if(this.slideCount <= 0) return;

      let last = this.getSlide(this.slideCount - 1);
      return last.offsetWidth + last.offsetLeft - this.container.offsetWidth;
   }

   // =============================================== //
   // Finding the slide index
   // =============================================== //
   
   /** 
    * @param {number} index
    * @returns {HTMLElement} 
    */
   getSlide(index){
      return this.container.children[index];
   }

   /** 
    * @param {number} offset
    * @returns {number} 
    */
   getSlideIndexFromOffset(offset){
      // This assumes children are in order of apearance. If this is not the case we need to sort the list

      if(this.container.children.length <= 1) return 0;

      for(let i = 0; i < this.container.children.length - 1; i++){
         /** @type {HTMLElement} */
         let first = this.container.children[i];
         /** @type {HTMLElement} */
         let next = this.container.children[i + 1];

         let centerX = (first.offsetLeft + next.offsetLeft) / 2;

         if(offset < centerX)
            return i;
      }

      // return the last child
      return this.container.children.length - 1;
   }
   /** 
    * @param {number} offset
    * @returns {HTMLElement} 
    */
   getSlideFromOffset(offset){
      let index = this.getSlideIndexFromOffset(offset);
      return this.container.children[index];
   }

   // =============================================== //
   // Requesting animations
   // =============================================== //
   _animate(){
      this.animationFrame = -1;
      this.animate();
   }
   _requestAnimationFrame(){
      if(this.animationFrame >= 0) return;
      this.animationFrame = requestAnimationFrame(this._animate.bind(this));
   }
   _cancelAnimationFrame(){
      if(this.animationFrame < 0) return;
      cancelAnimationFrame(this.animationFrame);
   }
}